'use client';

import { Modal, useScreenDetect } from '@sgcmpteltd/uikit-next';
import {
  setCache as setLocalStorageCache,
} from '@sgcmpteltd/uikit-react/utils/cache/local-storage';
import cx from 'clsx';
import Link from 'next/link';
import {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ACTION_STATUS_ERROR, ACTION_STATUS_SUCCESS } from '@/actions';
import {
  getPromotionShortlistForm,
} from '@/actions/new-car/promotion-shortlist-form/get';
import {
  postPromotionShortlistForm,
} from '@/actions/new-car/promotion-shortlist-form/post';
import {
  convertToDealerTypeAbbr,
} from '@/apis/server/new-car/v1/promotion-shortlist-form';
import type {
  CamelCaseResponseData as PromotionShortlistFormData,
} from '@/apis/server/new-car/v1/promotion-shortlist-form/get';
import type {
  CamelCasePromotionList,
} from '@/apis/server/new-car/v1/promotions';
import type {
  CamelCasePromotion,
} from '@/apis/server/new-car/v1/promotions/types';
import {
  PromotionDetailsModal,
} from '@/app/_components/page_content/new-car-promo/promotion-details-modal';
import {
  Step as CheckShortlistStep,
// eslint-disable-next-line @stylistic/max-len
} from '@/app/_components/page_content/new-car-promo/promotion-details-modal/step/check-shortlist';
import {
  Step as CheckShortlistResultStep,
// eslint-disable-next-line @stylistic/max-len
} from '@/app/_components/page_content/new-car-promo/promotion-details-modal/step/check-shortlist-result';
import { OguryScript } from '@/components/ogury-script';
import {
  SectionContainer,
  TITLE_HEADING_H2,
} from '@/components/section-container/base';
import { UserContext } from '@/context/user';
import { createTimestampUid } from '@/utils/id';
import { joinSgcmSite } from '@/utils/url/join/public';
import {
  getMemberLoginUrl,
  MEMBER_LOGIN_KEY_NEW_CAR_PROMOTION_ALERT,
} from '@/utils/url/member';

import {
  SECTION_TITLE_RECOMMENDED_FOR_YOU,
} from '../../../_components/page-content/constants';
import {
  RecommendedForYouForInfo,
} from '../../../_components/page-content/recommended-for-you-for-info';
import { AdPromoContent } from './ad-promo-content/component';
import { CarBasicInfo } from './car-basic-info';
import {
  CACHE_KEY_NEWCAR_PROMOTION_DATA,
  CACHE_TTL_NEWCAR_PROMOTION_DATA,
  DEALER_TYPE_AD,
  DEALER_TYPE_PI,
  MODAL_STEP_CHECK_SHORTLIST,
  MODAL_STEP_CHECK_SHORTLIST_RESULT,
  MODAL_TRANSITION_DURATION_MS_DEFAULT,
} from './constants';
import { NoCurrentPromo } from './no-current-promo';
import { PiPromoContent } from './pi-promo-content';
import { SponsoredContent } from './sponsored-content';
import styles from './styles.module.scss';
import type { Component, PostCheckShortlistResult } from './types';
import { getPromotionCarCode } from './utils';

export const PageContent: Component = ({
  promotionList,
  carDetail,
  modelId,
}) => {
  const [showCarBasicComponent,
    setShowCarBasicComponent] = useState<boolean>(false);

  const carPromos = (promotionList ?? []).reduce((acc, item) => {
    const matchingPromo = item.models
      .some(model => model.id === Number(modelId));

    if (matchingPromo) {
      return [...acc, item];
    }

    return acc;
  }, [] as CamelCasePromotionList);

  const [
    checkAddToShortlistData,
    setCheckAddToShortlistData,
  ] = useState<PromotionShortlistFormData | null>(null);

  const [
    modalAddToShortlistStep,
    setModalAddToShortlistStep,
  ] = useState('');

  const userContextData = useContext(UserContext);

  const [
    activePromotionListIndex,
    setActivePromotionListIndex,
  ] = useState<number | null>(null);

  const activePromotionDetailsItem = activePromotionListIndex !== null
    ? carPromos[activePromotionListIndex] as CamelCasePromotion
    : null;

  const [
    promotionDetailsModalKey,
    setPromotionDetailsModalKey,
  ] = useState(createTimestampUid);

  const [
    isPromotionDetailsModalOpen,
    setIsPromotionDetailsModalOpen,
  ] = useState(false);

  const [
    isAddToShortlistModalOpen,
    setIsAddToShortlistModalOpen,
  ] = useState(false);

  const [
    postCheckShortlistResultData,
    setPostCheckShortlistResultData,
  ] = useState<PostCheckShortlistResult | null>(null);

  const isBrowser = () => typeof window !== 'undefined';
  const rawUrl = isBrowser() ? window.location.href : '';
  const utf8Bytes = new TextEncoder().encode(rawUrl);
  const currentUrlInBase64 = Buffer.from(utf8Bytes).toString('base64');

  const handleAddToShortlist = async () => {
    const dealerTypeAbbr = carDetail?.dealerType
      ? convertToDealerTypeAbbr(carDetail.dealerType)
      : 'a';

    const promotionCarCode = getPromotionCarCode({
      carId: Number(modelId),
      dealerTypeAbbr,
    });

    if (!userContextData?.userId) {
      setLocalStorageCache({
        key: CACHE_KEY_NEWCAR_PROMOTION_DATA,
        value: {
          adpi: dealerTypeAbbr,
          isCommercial: carDetail?.authorizeDealers?.[0].isCommercial,
          carcode: promotionCarCode,
          carmake: carDetail?.make,
          uid: '',
        },
        ttl: CACHE_TTL_NEWCAR_PROMOTION_DATA,
      });

      window.location.assign(
        `${getMemberLoginUrl({
          key: MEMBER_LOGIN_KEY_NEW_CAR_PROMOTION_ALERT,
        }).toString()}&referrer=${currentUrlInBase64}`,
      );

      return;
    }

    const formData = new FormData();

    formData.set('agent', `${carDetail?.authorizeDealers?.[0].isCommercial ? 1 : 0}`);
    formData.set('carCode', promotionCarCode);
    formData.set('make', carDetail?.make ?? '');
    formData.set('dealerTypeAbbr', dealerTypeAbbr);

    const actionResult = await getPromotionShortlistForm(formData);

    if (actionResult.status !== ACTION_STATUS_SUCCESS) {
      return;
    }

    setCheckAddToShortlistData(actionResult.data);
    setModalAddToShortlistStep(MODAL_STEP_CHECK_SHORTLIST);
    setIsAddToShortlistModalOpen(true);
  };

  useEffect(() => {
    const toggleFilterModal = () => {
      if (window.location.hash === '') {
        setIsPromotionDetailsModalOpen(false);
      }
    };

    window.addEventListener('hashchange', toggleFilterModal);
  }, []);

  useEffect(() => {
    const adPromo = carPromos.find(
      promo => promo.dealer.type === DEALER_TYPE_AD,
    );

    if (adPromo && !showCarBasicComponent) {
      setShowCarBasicComponent(true);
    }
  }, [carPromos, showCarBasicComponent]);

  const promoRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();

  useEffect(() => {
    const { hash } = window.location;

    if (hash) {
      const id = hash.replace('#', '');

      const timer = setTimeout(() => {
        const targetRef = promoRefs.current[id];

        if (targetRef) {
          const yOffset = isDesktopMode ? -70 : -50;

          const y = targetRef
            .getBoundingClientRect().top + window.scrollY + yOffset;

          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 500);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isDesktopMode]);

  return (
    <main className={styles.containerPageRoot}>
      <OguryScript />
      <div className={styles.pageContainerRoot}>
        <div className={styles.promoContainer}>
          {carPromos.length === 0
            ? (
              <>
                <CarBasicInfo
                  carDetail={carDetail}
                  handleAddToShortlist={handleAddToShortlist} />
                <NoCurrentPromo handleAddToShortlist={handleAddToShortlist} />
              </>
            )
            : (
              <>
                <div>
                  { showCarBasicComponent
                  && (
                    <CarBasicInfo
                      carDetail={carDetail}
                      handleAddToShortlist={handleAddToShortlist} />
                  ) }
                  <div className={styles.containerAdPromo}>
                    {carPromos.map((adPromo, index) => (adPromo.dealer
                      .type === DEALER_TYPE_AD
                      && (
                        <div
                          key={adPromo.id}
                          ref={(el) => {
                            if (el) promoRefs.current[adPromo.id] = el;
                          }}
                          className={` ${styles.adPromItem} ${index % 2 === 1 && styles.backgroundIsGray}`}>
                          <AdPromoContent
                            item={adPromo}
                            modelId={modelId} />
                        </div>
                      )))}
                  </div>
                </div>
                <div className={styles.containerPiPromo}>
                  {carPromos.map((piPromo, index) => (piPromo.dealer
                    .type === DEALER_TYPE_PI
                    && (
                      <div
                        key={piPromo.id}
                        ref={(el) => {
                          if (el) promoRefs.current[piPromo.id] = el;
                        }}>
                        <PiPromoContent
                          index={index}
                          item={piPromo}
                          onItemClick={(newActivePromotionListIndex) => {
                            // eslint-disable-next-line @stylistic/max-len
                            setActivePromotionListIndex(newActivePromotionListIndex);
                            setIsPromotionDetailsModalOpen(true);
                          }} />
                      </div>
                    )))}
                </div>
              </>
            )}
        </div>
        <SponsoredContent
          modelId={Number(modelId)}
          promotions={promotionList} />
        <div className={styles.viewAllButton}>
          <Link
            href={`${joinSgcmSite('/new_cars/newcars_promotionslist.php')}`}
            target="_self">
            <div className={styles.linkViewAllPromos}>
              View All Promotions
            </div>
          </Link>
        </div>
        <PromotionDetailsModal
          key={promotionDetailsModalKey}
          isOpen={isPromotionDetailsModalOpen}
          promotion={activePromotionDetailsItem}
          setOpen={setIsPromotionDetailsModalOpen}
          onClose={() => {
            setIsPromotionDetailsModalOpen(false);
            window.history.pushState(null, '', ' ');
          }}
          onExit={() => {
            setPromotionDetailsModalKey(createTimestampUid());
          }} />
        <Modal
          className={cx(
            styles.containerRoot,
            styles.isNormal,
            {
              [styles.isCheckShortList]: modalAddToShortlistStep
              === MODAL_STEP_CHECK_SHORTLIST
              || modalAddToShortlistStep === MODAL_STEP_CHECK_SHORTLIST_RESULT,
            },
          )}
          contentClassName={styles.containerModalContent}
          headerControlClassName={styles.headerControlClassName}
          isOpen={isAddToShortlistModalOpen}
          title=""
          transitionDurationMs={MODAL_TRANSITION_DURATION_MS_DEFAULT}
          onClose={() => {
            setIsAddToShortlistModalOpen(false);
            window.history.pushState(null, '', ' ');
          }}>
          {
            modalAddToShortlistStep === MODAL_STEP_CHECK_SHORTLIST
            && !!checkAddToShortlistData
            && (
              <div
                className={cx(
                  styles.containerStep,
                  styles.isCheckShortlist,
                )}>
                <div
                  className={styles.containerStepContent}>
                  <CheckShortlistStep
                    alertPageLink={checkAddToShortlistData.alertPageLink}
                    checkList={checkAddToShortlistData.checkbox}
                    email={checkAddToShortlistData.email}
                    title={checkAddToShortlistData.title}
                    onSave={async ({
                      checkMap,
                    }) => {
                      const formData = new FormData();
                      const shortlistCheckEntries = Object.entries(checkMap);
                      const dealerTypeAbbr = carDetail?.dealerType
                        ? convertToDealerTypeAbbr(carDetail.dealerType)
                        : 'a';
                      const promotionCarCode = getPromotionCarCode({
                        carId: Number(modelId),
                        dealerTypeAbbr,
                      });

                      for (const [checkKey, isChecked]
                        of shortlistCheckEntries) {
                        formData.set(checkKey, `${+isChecked}`);
                      }

                      formData.set('agent', `${carDetail?.authorizeDealers?.[0].isCommercial ? 1 : 0}`);
                      formData.set('carCode', promotionCarCode);
                      formData.set('make', carDetail?.make ?? '');
                      formData.set(
                        'dealerTypeAbbr',
                        convertToDealerTypeAbbr(carDetail?.dealerType
                          ? carDetail?.dealerType
                          : 'ad'),
                      );

                      const actionResult = await postPromotionShortlistForm(
                        formData,
                      );

                      switch (actionResult.status) {
                        case ACTION_STATUS_SUCCESS:
                          setPostCheckShortlistResultData({
                            isSuccess: true,
                            title: actionResult.data,
                          });

                          break;
                        case ACTION_STATUS_ERROR:
                        default:
                          setPostCheckShortlistResultData({
                            isSuccess: false,
                            title: 'Error occurred',
                          });

                          break;
                      }

                      setModalAddToShortlistStep(
                        MODAL_STEP_CHECK_SHORTLIST_RESULT,
                      );
                    }} />
                </div>
              </div>
            )
          }
          {
            modalAddToShortlistStep === MODAL_STEP_CHECK_SHORTLIST_RESULT
            && !!checkAddToShortlistData
            && !!postCheckShortlistResultData
            && (
              <div
                className={cx(
                  styles.containerStep,
                  styles.isCheckShortlist,
                )}>
                <div
                  className={styles.containerStepContent}>
                  <CheckShortlistResultStep
                    alertPageLink={checkAddToShortlistData.alertPageLink}
                    isSuccess={postCheckShortlistResultData.isSuccess}
                    title={postCheckShortlistResultData.title}
                    onClose={() => {
                      setIsAddToShortlistModalOpen(false);
                      window.history.pushState(null, '', ' ');
                    }} />
                </div>
              </div>
            )
          }
        </Modal>
        <SectionContainer
          className={styles.containerPageContent}
          title={SECTION_TITLE_RECOMMENDED_FOR_YOU}
          titleHeading={TITLE_HEADING_H2}>
          <RecommendedForYouForInfo />
        </SectionContainer>
      </div>
    </main>
  );
};
